/**
 * AlertBell — sininho no topbar com count e dropdown dos alertas firing.
 *
 * Usa useActiveAlerts (refetch 30s).
 *  - admin/operator: alertas do próprio tenant
 *  - super_admin:    alertas de TODOS os tenants (com tag do tenant_slug)
 *  - viewer:         escondido
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, BellRing, Check, CheckCircle2 } from "lucide-react";
import clsx from "clsx";
import { toast } from "react-hot-toast";

import { useAuth } from "@/contexts/AuthContext";
import { useActiveAlerts } from "@/hooks/queries";
import { alertEventsApi } from "@/api/alerts";
import { apiErrorMessage } from "@/api/client";
import { Badge } from "./Badge";
import { timeAgo } from "@/lib/format";
import type { RuleKind } from "@/types";
import { SEVERIDADE_LABEL, SEVERIDADE_TOM } from "@/lib/severidade";
import { RULE_KIND_LABELS } from "@/lib/ruleKinds";

// Mapa compartilhado (lib/severidade.ts): este arquivo tinha a própria
// cópia, e eram dez ao todo. Ver o cabeçalho de lá.
const SEVERITY_TONE = SEVERIDADE_TOM;

export function AlertBell() {
  const auth = useAuth();
  // operator+ vê o sininho. viewer não.
  const showsAlerts = auth.hasMinRole("operator");
  // ACK é de operator+ : quem está no local às 3h é quem reconhece. Tem de
  // casar com o `require_operator` da rota — quando o front e a API
  // discordam, o botão some pra quem podia agir, ou aparece e dá 403.
  const canAck = auth.hasMinRole("operator");

  // super_admin não passa tenant_id → backend retorna alertas de todos os tenants.
  const active = useActiveAlerts(undefined);
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const qc = useQueryClient();
  const ackMut = useMutation({
    mutationFn: ({ ruleId, equipmentId }: { ruleId: string; equipmentId: string }) =>
      alertEventsApi.acknowledge(ruleId, equipmentId),
    onSuccess: () => {
      toast.success("Alerta reconhecido");
      // Invalida prefixo — pega active-alerts e alert-events de qualquer tenant
      void qc.invalidateQueries({ queryKey: ["alert-events"] });
      void qc.invalidateQueries({ queryKey: ["active-alerts"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!showsAlerts) return null;

  const alerts = active.data ?? [];
  // #159: badge no sininho conta SÓ os firing (não reconhecidos).
  // Alertas ack são vistos abaixo no dropdown com opacidade reduzida.
  const firingAlerts = alerts.filter((a) => a.status === "firing");
  const count = firingAlerts.length;
  const totalCount = alerts.length;
  const hasCritical = firingAlerts.some((a) => a.severity === "critical");

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className={clsx(
          "relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
          count > 0
            ? hasCritical
              ? "text-red-400 hover:bg-red-400/10"
              : "text-amber-400 hover:bg-amber-400/10"
            : "text-slate-400 dark:text-slate-500 hover:bg-white/5",
        )}
        onClick={() => setOpen((o) => !o)}
        aria-label={count > 0 ? `${count} alertas ativos` : "Sem alertas ativos"}
      >
        {count > 0 ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
        {count > 0 && (
          <span
            className={clsx(
              "absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white",
              hasCritical ? "bg-red-600" : "bg-amber-500",
            )}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-white/10 bg-[#0a0f1a] shadow-card-hover animate-fade-in">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
            <div className="text-sm font-semibold text-slate-100">
              {totalCount === 0
                ? "Sem alertas ativos"
                : count > 0
                  ? `${count} ${count > 1 ? "alertas ativos" : "alerta ativo"}${
                      totalCount > count ? ` · ${totalCount - count} reconhecido${totalCount - count > 1 ? "s" : ""}` : ""}`
                  : `${totalCount} reconhecido${totalCount > 1 ? "s" : ""}`}
            </div>
            {/* Ocorrências, e não /admin/alerts: o sino mostra o que ESTÁ
                acontecendo, e quem clica quer ver o que aconteceu — não a
                tela de configurar regras. */}
            <Link
              to="/ocorrencias"
              onClick={() => setOpen(false)}
              className="text-xs text-cyan-300 hover:text-cyan-200 hover:underline"
            >
              Ver todas
            </Link>
          </div>

          {alerts.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400 dark:text-slate-500">
              Tudo tranquilo por aqui.
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {alerts.slice(0, 8).map((a) => {
                const isAcked = a.status === "acknowledged";
                const ackInFlight =
                  ackMut.isPending &&
                  ackMut.variables?.ruleId === a.rule_id &&
                  ackMut.variables?.equipmentId === a.equipment_id;
                return (
                  <li
                    key={`${a.rule_id}-${a.equipment_id}`}
                    className={clsx(
                      "flex items-start gap-3 border-b border-white/10 px-4 py-3 last:border-b-0 hover:bg-white/5",
                      isAcked && "opacity-60",
                    )}
                  >
                    <span className="mt-0.5 flex-none">
                      <Badge tone={SEVERITY_TONE[a.severity]}>{SEVERIDADE_LABEL[a.severity] ?? a.severity}</Badge>
                    </span>
                    {/* A linha inteira leva ao detalhe daquele alerta. Sem
                        episódio (alerta aberto antes da v35) cai na lista. */}
                    <Link
                      to={a.episodio_id
                        ? `/ocorrencias?episodio=${a.episodio_id}`
                        : "/ocorrencias"}
                      onClick={() => setOpen(false)}
                      className="min-w-0 flex-1"
                    >
                      {/* Três linhas em vez de uma espremida: o nome do ativo
                          e o do cliente disputavam a mesma linha com o selo do
                          slug, e o nome saía "Camara F..." — justo a parte que
                          diz onde ir. O tipo sai em português, não o
                          identificador da regra. */}
                      <div className="flex items-start gap-1.5">
                        <span className="text-sm font-medium leading-snug text-slate-100 break-words">
                          {a.equipment_name}
                        </span>
                        {isAcked && (
                          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-emerald-400 flex-none" />
                        )}
                      </div>
                      <div className="text-xs text-slate-300">
                        {RULE_KIND_LABELS[a.rule_kind as RuleKind] ?? a.rule_kind}
                      </div>
                      <div className="text-xs text-slate-400 dark:text-slate-500">
                        {[auth.isSuperAdmin ? a.tenant_name : null, a.site_name, timeAgo(a.since)]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </Link>
                    {canAck && !isAcked && (
                      <button
                        type="button"
                        disabled={ackInFlight}
                        onClick={() =>
                          ackMut.mutate({
                            ruleId: a.rule_id,
                            equipmentId: a.equipment_id,
                          })
                        }
                        className={clsx(
                          "flex-none rounded-md px-2 py-1 text-xs font-medium transition-colors",
                          "border border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/10",
                          "disabled:opacity-50 disabled:cursor-wait",
                        )}
                        title="Reconhecer este alerta (para de re-notificar até recuperar)"
                      >
                        <Check className="h-3.5 w-3.5 inline" />
                      </button>
                    )}
                  </li>
                );
              })}
              {alerts.length > 8 && (
                <li className="border-t border-white/10 px-4 py-2 text-center">
                  <Link
                    to="/ocorrencias"
                    onClick={() => setOpen(false)}
                    className="text-xs text-cyan-300 hover:text-cyan-200 hover:underline"
                  >
                    +{alerts.length - 8} alertas — ver todas
                  </Link>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
